import React, { useEffect, useRef } from "react";
import { Card, Radio, Button, Divider } from "antd";
import "./style.scss";
import { FormattedMessage } from "react-intl";
import { AmexCardSvg, MaestroCardSvg, MasterCardSvg, VisaCardSvg } from "@assets";
import { AmazonPaySvg, ApplePaySvg, GooglePaySvg, LinkPaySvg, PaypalSvg } from "../../../assets/icons/payment-select";
import { IAccountPreference } from "@share/common-types";
import { ILoginState } from "@share/store/slices";
import { useSelector } from "react-redux";
import { FunctionalRootState } from "@share/utils";
import { isEmpty } from "lodash";

interface IProps {
  multiplePayments: any;
  setPaymentSelected: (payment: string) => void;
  scrollToPaymentSelect: boolean;
  scrollToPaymentSelectDisabled: () => void;
}

const PaymentMethodsSelect: React.FC<IProps> = ({ multiplePayments, setPaymentSelected, scrollToPaymentSelect, scrollToPaymentSelectDisabled }) => {
  const [selectedMethod, setSelectedMethod] = React.useState<string>('');
  const [isCollapsed, setIsCollapsed] = React.useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { account }: ILoginState = useSelector((state: FunctionalRootState) => state.loginStore);
  const buttonsPreferences = account?.preferences.filter(
    (x: IAccountPreference) => x.key === 'payment_buttons',
  );
  const PAYMENT_BUTTON_IS_EMPTY = isEmpty(buttonsPreferences);
  const handleChange = (e: any) => {
    setSelectedMethod(e.target.value);
  };

  const handleCollapse = () => {
    setPaymentSelected(selectedMethod);
    scrollToPaymentSelectDisabled();
    animateHeightChange(() => setIsCollapsed(true));
  };

  const handleExpand = () => {
    animateHeightChange(() => setIsCollapsed(false));
  };

  const animateHeightChange = (callback: () => void) => {
    const container = containerRef.current;
    if (!container) return;

    const startHeight = container.offsetHeight;
    callback();
    requestAnimationFrame(() => {
      const endHeight = container.offsetHeight;
      container.style.height = `${startHeight}px`;
      requestAnimationFrame(() => {
        container.style.transition = "height 0.3s ease-in-out";
        container.style.height = `${endHeight}px`;
        container.addEventListener(
          "transitionend",
          () => {
            container.style.height = "";
            container.style.transition = "";
          },
          { once: true }
        );
      });
    });
  };

  const renderOption = (value: string, label: string, Icon?: React.FC) => (
    <div className="payment-option">
      <Radio value={value} className="payment-methods-select__card-flex">
        <span className="option-text">{label}</span>
      </Radio>
      {Icon && <Icon />}
    </div>
  );

  const paymentOptions = [
    {
      value: "card",
      label: <FormattedMessage id="debit.credit.card" />,
      Icon: () => (
        <div className="payment-method-select__cards">
          <MaestroCardSvg />
          <MasterCardSvg />
          <VisaCardSvg />
          <AmexCardSvg />
        </div>
      ),
    },
    multiplePayments.googlePay && !PAYMENT_BUTTON_IS_EMPTY && {
      value: "google_play",// Is google_play because in the backend is misspelled
      label: <FormattedMessage id="googlepay.title" />,
      Icon: GooglePaySvg,
    },
    multiplePayments.applePay && !PAYMENT_BUTTON_IS_EMPTY && {
      value: "apple_pay",
      label: <FormattedMessage id="applepay.title" />,
      Icon: ApplePaySvg,
    },
    multiplePayments.link && !PAYMENT_BUTTON_IS_EMPTY && {
      value: "link",
      label: <FormattedMessage id="link.title" />,
      Icon: LinkPaySvg,
    },
    multiplePayments.paypal && !PAYMENT_BUTTON_IS_EMPTY && {
      value: "paypal",
      label: <FormattedMessage id="paypal.title" />,
      Icon: PaypalSvg,
    },
    multiplePayments.amazonPay && !PAYMENT_BUTTON_IS_EMPTY && {
      value: "amazon_pay",
      label: <FormattedMessage id="amazonpay.title" />,
      Icon: AmazonPaySvg,
    },
  ].filter(Boolean);

  const displayedOptions = isCollapsed
    ? paymentOptions.filter((option) => option.value === selectedMethod)
    : paymentOptions;

  useEffect(() => {
    if (PAYMENT_BUTTON_IS_EMPTY) {
      // If there are no button preferences, default to "card" and collapse the section
      setSelectedMethod("card");
      setTimeout(() => {
        setPaymentSelected("card");
      }, 1);
      if (!isCollapsed) {
        handleCollapse(); // This will animate and set isCollapsed to true
      }
    } else {
      // If button preferences exist, clear the selection and expand to show all options
      setSelectedMethod("");
      setPaymentSelected("");
      if (isCollapsed) {
        setIsCollapsed(false);
      }
    }
  }, [PAYMENT_BUTTON_IS_EMPTY]);

  return (
    <Card
      className={`payment-methods-select__card mb-3 ${scrollToPaymentSelect ? "error-border" : ""}`}
      id="payment-methods-select__card"
    >
      <p className="payment-subtitle"><FormattedMessage id="review.booking.payment.options.message" /></p>

      <div ref={containerRef}>
        <Radio.Group
          onChange={handleChange}
          value={selectedMethod}
          className={`payment-methods-select ${isCollapsed ? "collapsed" : ""}`}
        >
          {displayedOptions.map((option) => (
            <div key={option.value} className="payment-option-wrapper">
              {renderOption(option.value, option.label, option.Icon)}
              {!isCollapsed && <Divider className="payment-divider" />}
            </div>
          ))}
        </Radio.Group>
      </div>

      {isCollapsed && !PAYMENT_BUTTON_IS_EMPTY && (
        <Button
          type="default"
          block
          style={{ marginTop: 20 }}
          onClick={handleExpand}
        >
          <FormattedMessage id="review.booking.payment.change" />
        </Button>
      )}

      {scrollToPaymentSelect && (
        <p className="error-message"><FormattedMessage id="review.booking.payment.select" /></p>
      )}

      {(!isCollapsed || PAYMENT_BUTTON_IS_EMPTY) && (
        <Button
          type="primary"
          block
          style={{ marginTop: 20 }}
          disabled={!selectedMethod || PAYMENT_BUTTON_IS_EMPTY}
          onClick={handleCollapse}
        >
          <FormattedMessage id="review.booking.payment.continue" />
        </Button>
      )}
    </Card>
  );
};

export default PaymentMethodsSelect;
